import { useState, useEffect } from 'react';
import axios from 'axios';
import ManagerLayout from '../components/ManagerLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Check, X, Printer } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManagerExceptions = ({ manager, onLogout }) => {
  const [exceptions, setExceptions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState('pending');
  const [formData, setFormData] = useState({
    employee_id: '',
    location_id: '',
    reason: '',
    clock_in: '',
    clock_out: '',
  });
  const [reviewData, setReviewData] = useState({
    exception_id: '',
    manager_notes: '',
  });

  useEffect(() => {
    fetchExceptions();
    fetchEmployees();
    fetchLocations();
  }, [selectedTab]);

  const fetchExceptions = async () => {
    try {
      const token = sessionStorage.getItem('managerToken');
      const response = await axios.get(
        `${API}/exceptions${selectedTab !== 'all' ? `?status=${selectedTab}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setExceptions(response.data);
    } catch (error) {
      toast.error('Error fetching exceptions');
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = sessionStorage.getItem('managerToken');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleCreateException = async () => {
    if (
      !formData.employee_id ||
      !formData.location_id ||
      !formData.reason ||
      !formData.clock_in ||
      !formData.clock_out
    ) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const token = sessionStorage.getItem('managerToken');
      await axios.post(
        `${API}/exceptions`,
        {
          employee_id: formData.employee_id,
          location_id: formData.location_id,
          reason: formData.reason,
          clock_in: new Date(formData.clock_in).toISOString(),
          clock_out: new Date(formData.clock_out).toISOString(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Exception created successfully');
      setIsCreateDialogOpen(false);
      setFormData({
        employee_id: '',
        location_id: '',
        reason: '',
        clock_in: '',
        clock_out: '',
      });
      fetchExceptions();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Error creating exception'
      );
    }
  };

  const handleApproveException = async (exceptionId, notes) => {
    try {
      const token = sessionStorage.getItem('managerToken');
      await axios.put(
        `${API}/exceptions/${exceptionId}/approve`,
        { manager_notes: notes },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Exception approved');
      fetchExceptions();
    } catch (error) {
      toast.error('Error approving exception');
    }
  };

  const handleRejectException = async (exceptionId, notes) => {
    try {
      const token = sessionStorage.getItem('managerToken');
      await axios.put(
        `${API}/exceptions/${exceptionId}/reject`,
        { manager_notes: notes },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Exception rejected');
      fetchExceptions();
    } catch (error) {
      toast.error('Error rejecting exception');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const ExceptionCard = ({ exception }) => {
    const [notes, setNotes] = useState('');
    const isPending = exception.status === 'pending';

    return (
      <Card
        className="p-6 mb-4"
        data-testid={`exception-card-${exception.id}`}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              {exception.employee_name}
            </h3>
            <p className="text-sm text-gray-600">
              Employee ID: {exception.employee_number}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Submitted: {formatDateTime(exception.created_at)}
            </p>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${
              exception.status === 'pending'
                ? 'bg-yellow-100 text-yellow-800'
                : exception.status === 'approved'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {exception.status}
          </span>
        </div>

        <div className="space-y-2 mb-4 text-sm">
          <div className="flex gap-2">
            <span className="text-gray-600 font-medium">Reason:</span>
            <span className="text-gray-800">{exception.reason}</span>
          </div>
          {exception.location_name && (
            <div className="flex gap-2">
              <span className="text-gray-600 font-medium">Location:</span>
              <span className="text-gray-800">
                {exception.location_name}
              </span>
            </div>
          )}
          {exception.clock_in && (
            <div className="flex gap-2">
              <span className="text-gray-600 font-medium">Clock In:</span>
              <span className="text-gray-800">
                {formatDateTime(exception.clock_in)}
              </span>
            </div>
          )}
          {exception.clock_out && (
            <div className="flex gap-2">
              <span className="text-gray-600 font-medium">Clock Out:</span>
              <span className="text-gray-800">
                {formatDateTime(exception.clock_out)}
              </span>
            </div>
          )}
          <div className="flex gap-2">
            <span className="text-gray-600 font-medium">Requested By:</span>
            <span className="text-gray-800 capitalize">
              {exception.requested_by}
            </span>
          </div>
          {exception.manager_notes && (
            <div className="mt-3 p-3 bg-gray-50 rounded">
              <span className="text-xs text-gray-600 font-medium">Manager Notes:</span>
              <p className="text-sm text-gray-800 mt-1">
                {exception.manager_notes}
              </p>
            </div>
          )}
        </div>

        {isPending && (
          <div className="space-y-3">
            <Textarea
              placeholder="Add notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full"
              data-testid={`exception-notes-${exception.id}`}
            />
            <div className="flex gap-3">
              <Button
                onClick={() => handleApproveException(exception.id, notes)}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid={`approve-exception-${exception.id}`}
              >
                <Check className="w-4 h-4 mr-2" />
                Approve
              </Button>
              <Button
                onClick={() => handleRejectException(exception.id, notes)}
                variant="destructive"
                className="flex-1"
                data-testid={`reject-exception-${exception.id}`}
              >
                <X className="w-4 h-4 mr-2" />
                Reject
              </Button>
            </div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <ManagerLayout manager={manager} onLogout={onLogout}>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Exceptions</h1>
          <p className="text-gray-600">Manage time entry exceptions</p>
        </div>

        <div className="flex gap-3">
          <Button
            onClick={handlePrint}
            variant="outline"
            data-testid="print-exceptions-button"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>

          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-gradient-to-r from-gray-900 to-black hover:from-gray-800 hover:to-gray-900 text-white shadow-lg"
                data-testid="create-exception-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Exception
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-exception-dialog">
            <DialogHeader>
              <DialogTitle>Create Exception</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employee
                </label>
                <Select
                  value={formData.employee_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, employee_id: value })
                  }
                >
                  <SelectTrigger data-testid="exception-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.full_name} ({employee.employee_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <Select
                  value={formData.location_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, location_id: value })
                  }
                >
                  <SelectTrigger data-testid="exception-location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason
                </label>
                <Textarea
                  placeholder="Enter reason for exception"
                  value={formData.reason}
                  onChange={(e) =>
                    setFormData({ ...formData, reason: e.target.value })
                  }
                  data-testid="exception-reason-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Clock In
                </label>
                <Input
                  type="datetime-local"
                  value={formData.clock_in}
                  onChange={(e) =>
                    setFormData({ ...formData, clock_in: e.target.value })
                  }
                  data-testid="exception-clock-in-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Clock Out
                </label>
                <Input
                  type="datetime-local"
                  value={formData.clock_out}
                  onChange={(e) =>
                    setFormData({ ...formData, clock_out: e.target.value })
                  }
                  data-testid="exception-clock-out-input"
                />
              </div>
              <Button
                onClick={handleCreateException}
                className="w-full"
                data-testid="submit-create-exception"
              >
                Create Exception
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">
            Approved
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">
            Rejected
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">
            All
          </TabsTrigger>
        </TabsList>

        <TabsContent value={selectedTab}>
          {exceptions.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-600">No exceptions found</p>
            </Card>
          ) : (
            exceptions.map((exception) => (
              <ExceptionCard key={exception.id} exception={exception} />
            ))
          )}
        </TabsContent>
      </Tabs>
    </ManagerLayout>
  );
};

export default ManagerExceptions;